import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Box, Typography, TextField  } from '@mui/material';
import { useNotificationContext } from 'contexts/NotificationContext';
import feedbackService from 'services/feedbackService';
import Drawer from 'features/IRWorkstation/components/Common/Drawer/Drawer';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';


const FeedbackDrawer = ({ open, onClose, tenant }) => {
	const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });
	const { showNotification } = useNotificationContext();

	const [saving, setSaving] = useState(false);
	const [feedback, setFeedback] = useState('');

	const MAX_CHARS = 600;

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.FeedbackDrawer);
	}, []);

	const handleSave = async () => {
		// this should be impossible but handling this gracefully if it does manage to happen
		if (!feedback || feedback.trim().length === 0) {
			return;
		}
		
		handleButtonClickTrackEvent('submit');

		setSaving(true);
		try {
			await feedbackService.submitFeedback(
				feedback,
				'IEM'
			);

			showNotification('Feedback submitted, thanks for helping improve the Investor Engagement Manager', 'success');
			onClose();
		} catch (err) {
			showNotification(
				err?.message || 'Failed to submit feedback',
				'error'
			);
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		handleButtonClickTrackEvent('cancel');
		onClose();
	};

	const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			cancel: ButtonClickTargets.FeedbackDrawerCancel,
			submit: ButtonClickTargets.FeedbackDrawerSubmit,
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

	const defaultActionButtons = [
		{
			key: 'feedback-cancel-action-button',
			label: 'Cancel',
			onClick: handleCancel,
			disabled: saving,
			variant: 'secondary',
		},
		{
			key: 'feedback-submit-action-button',
			label: 'Submit Feedback',
			onClick: handleSave,
			loading: saving,
			disabled: feedback.trim().length === 0,
			variant: 'primary',
		},
	];

	return (
		<Drawer
			open={open}
			onClose={handleCancel}
			title='Submit Feedback'
			subtitle=''
			actionButtons={defaultActionButtons}
		>
			<Box
				sx={{
					padding: '18px'
				}}
			>
				<Typography sx={{ fontSize: '13px', paddingBottom: '10px' }}>
					Provide feedback below, anything that you submit will be sent directly to the Altvia Product team.
				</Typography>
				<TextField
					type='text'
					size='medium'
					fullWidth
					multiline
					minRows={9}
					maxRows={9}
					value={feedback}
					onChange={(e) => setFeedback(e.target.value)}
					disabled={false}
					placeholder='Submit feedback to Altvia'
					helperText={`${feedback.length}/${MAX_CHARS}`}
					slotProps={{
						htmlInput: { maxLength: MAX_CHARS }
					}}
				/>
			</Box>
			
		</Drawer>
	);
};

FeedbackDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	tenant: PropTypes.object
};

export default FeedbackDrawer;
